import * as React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';

import { Box, SpinDotsLoader } from '@clutter/clean';
import { List } from '@clutter/clean';

import { Spacer } from '@shared/components/helpers';
import { ConfigWithSetup } from '@shared/components/inventory_capture';
import { IInventoryConfig } from '@shared/types/inventory_config';
import { IOrder } from '@shared/types/order';
import { OrderStatus } from '@shared/types/order';

import { Header } from '@portal/components/helpers/header';
import { orderURL } from '@portal/config/routes';
import {
  OrderServiceTypeEnum,
  OrderDetailsQueryVariables,
  DropOff__Appointment,
  OrderTypeEnum,
  OrderSubtypeEnum,
  useShipmentLabelsQuery,
  useUncancellableShipmentOrderIDsQuery,
  OrderDetailsDocument,
} from '@portal/schema';
import { IAccount } from '@portal/types/account';
import { buildOrderInventory } from '@portal/utils';
import { dateDetailsMap } from '@portal/utils/scheduling';

import { Banner } from '../shared/banner';
import { Address } from './details/address';
import { Alerts } from './details/alerts';
import { Cancel } from './details/cancel';
import { Contact } from './details/contact';
import { Scheduled as DisposalScheduled } from './details/disposal/scheduled';
import { DoorstepNotice } from './details/doorstep_notice';
import { AddressView as DropOffAddress } from './details/drop_off/address_view';
import { Scheduled as DropOffScheduled } from './details/drop_off/scheduled';
import { AddressView as FacilityAddressView } from './details/facility/address_view';
import { Contact as FacilityContact } from './details/facility/contact';
import { Scheduled as FacilityScheduled } from './details/facility/scheduled';
import { Inventory } from './details/inventory';
import { Plan } from './details/plan';
import { Scheduled } from './details/scheduled';
import { AddressView as ShipmentAddress } from './details/shipping/address_view';
import { Contact as ShipmentContact } from './details/shipping/contact';
import { PrintLabels as ShipmentPrintLabels } from './details/shipping/print_labels';
import { Scheduled as ShipmentScheduled } from './details/shipping/scheduled';
import { Tracking as ShipmentTracking } from './details/shipping/tracking';
import { WaitlistRequests } from './details/waitlist_requests';
import { Documents } from './documents';
import { Home } from './home';
import { DisposalLineItem } from './home/appointment_details/info_list/disposal_line_item';
import { MultiDayTimeline } from './home/appointment_details/info_list/multi_day_timeline';
import { LaborCostDetails } from './labor_cost_details';
import { PerItemPricingCosts } from './per_item_pricing_costs';

const LoaderContainer = styled(Box)`
  width: 100%;
  text-align: center;
`;

interface IQueryData extends IInventoryConfig {
  account: IAccount;
  order: IOrder;
}

type DropOffDetailsProps = {
  account: IAccount;
  order: IOrder;
  dropOffAppointment: DropOff__Appointment;
  config: IInventoryConfig;
  onboarding: boolean;
};

const DropOffDetails: React.FC<DropOffDetailsProps> = ({ account, order, dropOffAppointment, config, onboarding }) => (
  <>
    <DropOffScheduled order={order} dropOffAppointment={dropOffAppointment} />

    {onboarding && <Plan order={order} />}

    <DropOffAddress fulfiller={dropOffAppointment.facility.fulfiller} address={dropOffAppointment.facility.address} />

    <Contact account={account} order={order} />

    {order.services.map((service, key) => (
      <ConfigWithSetup key={key} {...config} inventory={buildOrderInventory(service, config)}>
        <Inventory order={order} service={service} />
      </ConfigWithSetup>
    ))}

    {order.perItemPricingInfo && (
      <>
        <Header tag="h4">Pricing Information</Header>
        <PerItemPricingCosts pricingInfo={order.perItemPricingInfo} />
        <hr />
      </>
    )}

    <Documents orderID={order.id} />

    <Cancel order={order} />
  </>
);

const ShipmentDetails: React.FC<{
  account: IAccount;
  order: IOrder;
  config: IInventoryConfig;
}> = ({ account, order, config }) => {
  const { data, loading } = useShipmentLabelsQuery({ variables: { orderId: order.id } });
  const { data: uncancellableData, loading: uncancellableLoading } = useUncancellableShipmentOrderIDsQuery({
    variables: { orderIDs: [order.id] },
  });

  if (loading || uncancellableLoading || !data || !uncancellableData) {
    return (
      <LoaderContainer>
        <SpinDotsLoader />
      </LoaderContainer>
    );
  }

  const labels = data.shipmentLabels;
  const uncancellable = uncancellableData.uncancellableShipmentOrderIds.length > 0;

  if (order.type === OrderTypeEnum.Return) {
    return (
      <>
        <ShipmentScheduled order={order} />

        <ShipmentAddress address={{ address: order.address }} type={order.type} />

        <ShipmentContact account={account} order={order} />

        {order.services.map((service, key) => (
          <>
            <ShipmentTracking service={service} />
            <ConfigWithSetup key={key} {...config} inventory={buildOrderInventory(service, config)}>
              <Inventory order={order} service={service} />
            </ConfigWithSetup>
          </>
        ))}

        <Documents orderID={order.id} />

        <Cancel order={order} />
      </>
    );
  } else {
    return (
      <>
        <ShipmentPrintLabels order={order} labels={labels} />

        <ShipmentAddress address={{ address: order.address }} type={order.type} />

        <ShipmentContact account={account} order={order} />

        <Documents orderID={order.id} />

        {!uncancellable && <Cancel order={order} />}
      </>
    );
  }
};

const FacilityOrderDetails: React.FC<{
  account: IAccount;
  order: IOrder;
  config: IInventoryConfig;
}> = ({ account, order, config }) => (
  <>
    <FacilityScheduled order={order} />
    <FacilityAddressView order={order} />
    <FacilityContact account={account} order={order} />

    {order.services.map((service, key) => (
      <ConfigWithSetup key={key} {...config} inventory={buildOrderInventory(service, config)}>
        <Inventory order={order} service={service} />
      </ConfigWithSetup>
    ))}

    <Documents orderID={order.id} />
    <Cancel order={order} />
  </>
);

const DisposalOrderDetails: React.FC<{
  order: IOrder;
  config: IInventoryConfig;
}> = ({ order, config }) => (
  <>
    <DisposalScheduled order={order} />

    {order.services.map((service, key) => (
      <ConfigWithSetup key={key} {...config} inventory={buildOrderInventory(service, config)}>
        <Inventory order={order} service={service} />
      </ConfigWithSetup>
    ))}

    <Documents orderID={order.id} />
    <Cancel order={order} />
  </>
);

export const Details: React.FC<RouteComponentProps<{ orderID: string }>> = (props) => {
  const {
    match: {
      params: { orderID },
    },
  } = props;
  const { data } = useQuery<IQueryData, OrderDetailsQueryVariables>(OrderDetailsDocument, {
    variables: { orderID },
    fetchPolicy: 'cache-and-network',
  });

  if (!data || !data.account || !data.order) {
    return null;
  }

  const { account, order, ...config } = data;
  const { dropOffAppointment } = order;
  const onboarding = order.type === OrderTypeEnum.Pickup && order.subtype === OrderSubtypeEnum.Onboarding;
  const move = order.type === OrderTypeEnum.Move;
  const supplyKit = order.type === OrderTypeEnum.RetailDelivery && order.subtype === OrderSubtypeEnum.SupplyKit;
  const shipment = order.serviceType === OrderServiceTypeEnum.Shipment;
  const facility = order.serviceType === OrderServiceTypeEnum.Facility;
  const disposal = order.serviceType === OrderServiceTypeEnum.Disposal || order.type === OrderTypeEnum.Disposal;
  const pending = order.status === OrderStatus.Pending;
  const scheduled = order.status === OrderStatus.Scheduled;
  const onWaitlist = pending || scheduled;
  const canAddDisposal = order.permissions.addDisposal;
  const timelineDetailsMap = dateDetailsMap(order.nonTravelSourceAndSuccessors ?? []);

  if (order.longDistanceMove && order.longDistanceMove.pickup.id !== orderID) {
    return <Redirect to={orderURL(order.longDistanceMove.pickup.id)} />;
  }

  if (onboarding || move || supplyKit) {
    return <Home {...props} />;
  }

  if (dropOffAppointment) {
    return (
      <DropOffDetails
        account={account}
        order={order}
        dropOffAppointment={dropOffAppointment}
        config={config}
        onboarding={onboarding}
      />
    );
  }

  if (shipment) {
    return <ShipmentDetails account={account} order={order} config={config} />;
  }

  if (facility) {
    return <FacilityOrderDetails account={account} order={order} config={config} />;
  }

  if (disposal) {
    return <DisposalOrderDetails order={order} config={config} />;
  }

  return (
    <>
      <Alerts config={config} order={order} />
      {!pending && !order.multiDay && <Scheduled order={order} />}
      {!pending && order.multiDay && (
        <>
          <Spacer height="2rem" />
          <Banner
            text={
              'Due to the size of your order, it has been split into multiple appointments. Contact us if you need to reschedule.'
            }
          />
          <Spacer height="2rem" />
          <MultiDayTimeline timelineDetailsMap={timelineDetailsMap} showArrivalTimes={true} sizing="large" />
          <Spacer height="2rem" />
        </>
      )}
      {onWaitlist && <WaitlistRequests order={order} isFirstEntry={pending} />}
      {order.serviceType === OrderServiceTypeEnum.Doorstep && <DoorstepNotice />}
      {onboarding && <Plan order={order} />}
      <Address order={order} />
      <Contact account={account} order={order} />
      {order.services.map((service, key) => (
        <ConfigWithSetup key={key} {...config} inventory={buildOrderInventory(service, config)}>
          <Inventory order={order} service={service} />
        </ConfigWithSetup>
      ))}
      {order.perItemPricingInfo && (
        <>
          <Header tag="h4">Pricing Information</Header>
          <PerItemPricingCosts pricingInfo={order.perItemPricingInfo} />
          <hr />
        </>
      )}
      {!order.perItemPricingInfo && order.laborCostDetails && (
        <>
          <Header tag="h4">Pricing Information</Header>
          <LaborCostDetails laborCostDetails={order.laborCostDetails} />
          {order.coupon && (
            <div>
              <b>Coupon:</b> {order.coupon.discountDescription}
            </div>
          )}
          <hr />
        </>
      )}
      {order.type === OrderTypeEnum.Pickup && (canAddDisposal || order.childDisposal) && (
        <>
          <Header tag="h4">Optional Service Add-ons</Header>
          <List>
            <DisposalLineItem order={order} />
          </List>
          <hr />
        </>
      )}
      <Documents orderID={order.id} />
      <Cancel order={order} />
    </>
  );
};
